/**
 * TaskFormModal - the create / edit dialog for the Tasks page (plan
 * plans/2026-08-08-reminders-and-tasks.md section 5).
 *
 * One component for both modes because the field set is identical:
 * title (required), description (the SAME TipTap editor the composer
 * uses, capturing text + html like internal notes), due date (native
 * date input), and an optional ticket link via the searchable picker.
 *
 * Attachments are edit-mode only: files need a task id to upload
 * against, so the create flow is "create, then Edit to attach" (plan
 * section 5). The edit modal lists existing files with delete and an
 * add control using the same one-POST-per-file pattern as the ticket
 * composer, with the 10 MB client-side hint.
 */
import { useEffect, useRef, useState } from "react";

import { Button, Input, Modal } from "@/components/ui";
import { RichTextEditor } from "@/components/tickets/RichTextEditor";
import {
  TaskTicketPicker,
  type TaskTicketValue,
} from "@/components/tasks/TaskTicketPicker";
import {
  useDeleteTaskAttachment,
  useTaskAttachments,
  useUploadTaskAttachment,
} from "@/hooks/useTaskAttachments";
import { useCreateTask, useUpdateTask } from "@/hooks/useTaskMutations";
import { plainTextToEditorHtml } from "@/lib/commentEditorSeed";
import { TASK_CATEGORY_OPTIONS } from "@/components/tasks/TaskCategoryBadge";
import type { TaskCategory, TaskResponse } from "@/types/api";

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export interface TaskFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The task being edited; null / undefined means create mode. */
  task?: TaskResponse | null;
}

/** Attachment manager rendered inside the edit modal. Kept separate
 * so the hooks only mount when there is a task id to scope them to. */
function TaskAttachmentsSection({ taskId }: { taskId: string }): JSX.Element {
  const attachments = useTaskAttachments(taskId);
  const upload = useUploadTaskAttachment(taskId);
  const remove = useDeleteTaskAttachment(taskId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    // Allow re-picking the same file after a failure.
    e.target.value = "";
    setFileError(null);
    for (const file of files) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setFileError(`${file.name} exceeds the 10 MB per-file limit.`);
        continue;
      }
      upload.mutate(file, {
        onError: (err) => {
          setFileError(
            err instanceof Error ? err.message : "Upload failed.",
          );
        },
      });
    }
  }

  const rows = attachments.data ?? [];

  return (
    <div>
      <span className="block text-sm font-medium text-neutral-700">
        Attachments
      </span>
      {rows.length === 0 && !attachments.isLoading && (
        <p className="mt-1 text-xs text-neutral-500">No files attached.</p>
      )}
      {rows.length > 0 && (
        <ul className="mt-1 divide-y divide-neutral-100 rounded-md border border-neutral-200">
          {rows.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 px-3 py-2 text-sm"
              data-testid="task-attachment-row"
            >
              <i
                className="fa-duotone fa-thin fa-paperclip text-neutral-400"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate" title={a.filename}>
                {a.filename}
              </span>
              <span className="shrink-0 text-xs text-neutral-500">
                {formatBytes(a.byte_size)}
              </span>
              <button
                type="button"
                onClick={() => remove.mutate(a.id)}
                className="shrink-0 rounded-md p-1 text-neutral-400 transition-colors hover:text-red-600"
                aria-label={`Remove ${a.filename}`}
              >
                <i
                  className="fa-duotone fa-thin fa-trash-can text-sm"
                  aria-hidden="true"
                />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          isLoading={upload.isPending}
          onClick={() => fileRef.current?.click()}
        >
          <i
            className="fa-duotone fa-thin fa-paperclip"
            aria-hidden="true"
          />
          Add file
        </Button>
        <span className="text-xs text-neutral-500">10 MB max per file</span>
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={onPick}
          data-testid="task-attachment-input"
        />
      </div>
      {fileError && (
        <p className="mt-1 text-xs text-red-600">{fileError}</p>
      )}
    </div>
  );
}

export function TaskFormModal({
  isOpen,
  onClose,
  task,
}: TaskFormModalProps): JSX.Element | null {
  const isEdit = !!task;

  const [title, setTitle] = useState("");
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [descriptionText, setDescriptionText] = useState("");
  const [dueDate, setDueDate] = useState("");
  // "" = no category (sent as null).
  const [category, setCategory] = useState<TaskCategory | "">("");
  const [ticket, setTicket] = useState<TaskTicketValue | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateTask();
  const update = useUpdateTask();
  const saving = create.isPending || update.isPending;

  // Re-seed the fields each time the modal opens (or the target task
  // changes) so a reopened create modal starts blank and an edit
  // modal reflects the row as it stands. Seeding the editor from
  // description_text when there is no html mirrors the note editor's
  // fallback (lib/commentEditorSeed.ts).
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (task) {
      setTitle(task.title);
      setDescriptionHtml(
        task.description_html ?? plainTextToEditorHtml(task.description_text),
      );
      setDescriptionText(task.description_text);
      setDueDate(task.due_date ?? "");
      setCategory(task.category ?? "");
      setTicket(
        task.ticket_public_code
          ? {
              public_code: task.ticket_public_code,
              subject: task.ticket_subject ?? "",
            }
          : null,
      );
    } else {
      setTitle("");
      setDescriptionHtml("");
      setDescriptionText("");
      setDueDate("");
      setCategory("");
      setTicket(null);
    }
  }, [isOpen, task]);

  if (!isOpen) return null;

  function submit() {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Title is required.");
      return;
    }
    // TipTap reports "<p></p>" for an empty document; the text mirror
    // is the honest emptiness check, matching how internal notes
    // decide whether to send html.
    const hasBody = descriptionText.trim().length > 0;
    const payload = {
      title: trimmedTitle,
      description_text: hasBody ? descriptionText : "",
      description_html: hasBody ? descriptionHtml : null,
      due_date: dueDate || null,
      category: category || null,
      ticket_ref: ticket?.public_code ?? null,
    };
    const onError = (err: Error) => {
      setError(err.message || "Save failed.");
    };
    if (isEdit && task) {
      update.mutate(
        { taskId: task.id, body: payload },
        { onSuccess: onClose, onError },
      );
    } else {
      create.mutate(payload, { onSuccess: onClose, onError });
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Task" : "New Task"}
      size="lg"
      data-testid="task-form-modal"
    >
      <div className="space-y-4">
        <Input
          id="task-title"
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What needs doing?"
          maxLength={500}
        />
        <div>
          <span className="block text-sm font-medium text-neutral-700">
            Description
          </span>
          <div className="mt-1">
            <RichTextEditor
              id="task-description"
              ariaLabel="Task description"
              value={descriptionHtml}
              onChange={(html, text) => {
                setDescriptionHtml(html);
                setDescriptionText(text);
              }}
              placeholder="Optional details..."
              minHeight="8rem"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            id="task-due-date"
            label="Due date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <div>
            <label
              htmlFor="task-category"
              className="block text-sm font-medium text-neutral-700"
            >
              Category
            </label>
            {/* Bare select (not ui/Select) for the same reason as the
                ReminderControl day-part select: ui/* mirrors Auware
                and we do not extend it. "Reminder" only appears as an
                option when the task already carries it (it is
                system-stamped by the fire loop, never hand-picked). */}
            <select
              id="task-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as TaskCategory | "")}
              className="mt-1 block w-full rounded-md border border-neutral-300 py-1.5 pl-2 pr-8 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              data-testid="task-category-select"
            >
              <option value="">None</option>
              {TASK_CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
              {task?.category === "reminder" && (
                <option value="reminder">Reminder</option>
              )}
            </select>
          </div>
        </div>
        <TaskTicketPicker
          id="task-ticket-link"
          label="Linked ticket"
          value={ticket}
          onChange={setTicket}
        />
        {isEdit && task ? (
          <TaskAttachmentsSection taskId={task.id} />
        ) : (
          <p className="text-xs text-neutral-500">
            Attachments can be added after the task is created (open it
            with Edit).
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-neutral-100 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            isLoading={saving}
            data-testid="task-form-submit"
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default TaskFormModal;
